// =============================================================================
// app/src/lib/buscaDoMapa.ts — a busca da aba do mapa (desenho "Onde fica",
// fase 2, 06/10/2026).
//
// A aba do mapa passa a usar o MESMO motor do balcão (buscar_pecas): acha por
// código de fábrica, OEM, barras, carro ("celta 2012") e erro de digitação, na
// ordem de relevância. Antes ela filtrava a lista local por "contém" e achava
// "ol" dentro de gOL e de ORIGINAL, em ordem de UUID.
//
// A RPC devolve só o lugar PRINCIPAL; os extras (092) vêm do cruzamento com
// pecasNoMapa, que a tela já carrega. Aqui ficam as contas puras desse
// cruzamento e a busca de LUGAR no mesmo campo, para o teste conferir sem tela.
// =============================================================================
import { AREAS, descreverEndereco, type Area, type LugarComEndereco } from './enderecos';
import type { CodigoZona, Objeto } from './loja3d';
import { ganchosDaArea, type AreaDaMarca } from './marcasDasCalotas';
import { temLugar } from './ondeFica';
import { ondeMora } from './zonas';

/** O que a tela precisa de uma linha da busca — a da RPC ou a da reserva local. */
export interface LinhaDaBusca {
  product_id: string;
  sku: string;
  descricao: string;
  marca: string | null;
  quantidade: number;
}

/** Uma linha de pecasNoMapa: a peça uma vez por lugar (o extra marcado). */
export interface PecaComLocal {
  product_id: string;
  local: string | null;
  extra?: boolean;
  /** Calota (15/10/2026): a área da marca dela. A linha é o endereço gravado. */
  area?: AreaDaMarca;
}

/**
 * Um lugar da peça que o MAPA conhece: há um objeto desenhado com o código, ou
 * ao menos a zona escrita ("Z3-G-1-4"). Objeto desenhado fora das zonas (uma
 * parede) não tem zona, e ainda assim é lugar: o desenho vai até ele.
 */
export interface LugarAchado {
  codigo: string;
  extra: boolean;
  zona: CodigoZona | null;
  objeto: Objeto | null;
}

/** Uma peça achada, numa linha só, com TODOS os lugares dela no mapa. */
export interface PecaAchada extends LinhaDaBusca {
  /** O principal primeiro, depois os extras. Vazio = sem lugar no mapa. */
  lugares: LugarAchado[];
  /**
   * Sem lugar no mapa, o que está escrito hoje ("loja", "G-1-4") — a linha diz
   * "hoje: loja". Null quando não há nada escrito.
   */
  hoje: string | null;
  /**
   * Calota (15/10/2026): o nome da marca cuja ÁREA são os `lugares` — os
   * ganchos todos da marca, não lugares dela um a um. A tela mostra a área.
   */
  area?: string;
}

export const semLugarNoMapa = (p: PecaAchada): boolean => p.lugares.length === 0;

/** Gancho de calota (GC-…): na calota com área, o gravado num gancho fica velho e não conta. */
const ehGanchoDeCalota = (codigo: string | null | undefined) => /^\s*GC-/i.test(codigo ?? '');

/**
 * As linhas da busca, cruzadas com pecasNoMapa: uma linha por peça, na ordem
 * em que a busca devolveu (a da relevância), com o principal e os extras.
 *
 * - "loja" e vazio não são lugar (lib/ondeFica.ts, temLugar);
 * - código que o mapa não reconhece (nem objeto, nem zona) também não: a peça
 *   fica "sem lugar no mapa", com o código em `hoje`;
 * - peça que veio da busca e NÃO está em pecasNoMapa (cadastrada sem linha de
 *   estoque) é "sem lugar": o mapa não tem de onde tirar um lugar para ela.
 */
export function juntarResultados(
  linhas: readonly LinhaDaBusca[],
  pecas: readonly PecaComLocal[],
  objetos: readonly Objeto[],
): PecaAchada[] {
  const porPeca = new Map<string, { principal: string | null; extras: string[]; area?: AreaDaMarca }>();
  for (const p of pecas) {
    const atual = porPeca.get(p.product_id) ?? { principal: null, extras: [] };
    if (p.area) atual.area = p.area;
    if (p.extra) {
      if (p.local && !atual.extras.includes(p.local)) atual.extras.push(p.local);
    } else {
      atual.principal = p.local;
    }
    porPeca.set(p.product_id, atual);
  }

  const vistas = new Set<string>();
  const saida: PecaAchada[] = [];
  for (const l of linhas) {
    if (vistas.has(l.product_id)) continue;
    vistas.add(l.product_id);
    const base: LinhaDaBusca = {
      product_id: l.product_id, sku: l.sku, descricao: l.descricao, marca: l.marca, quantidade: l.quantidade,
    };
    const daPeca = porPeca.get(l.product_id);
    if (!daPeca) {
      saida.push({ ...base, lugares: [], hoje: null });
      continue;
    }
    const lugares: LugarAchado[] = [];
    const naoReconhecidos: string[] = [];
    // Calota na ÁREA DA MARCA: os lugares são os ganchos da marca (o gancho
    // gravado fica velho); a pilha de reserva gravada continua valendo.
    const candidatos = daPeca.area
      ? [
          ...ganchosDaArea(daPeca.area).map((codigo, i) => ({ codigo, extra: i > 0 })),
          ...[daPeca.principal, ...daPeca.extras].filter(temLugar)
            .filter((c) => !ehGanchoDeCalota(c)).map((codigo) => ({ codigo: codigo.trim(), extra: true })),
        ]
      : [
          ...(temLugar(daPeca.principal) ? [{ codigo: daPeca.principal.trim(), extra: false }] : []),
          ...daPeca.extras.filter(temLugar).map((codigo) => ({ codigo: codigo.trim(), extra: true })),
        ];
    for (const c of candidatos) {
      if (lugares.some((x) => x.codigo.toUpperCase() === c.codigo.toUpperCase())) continue;
      const onde = ondeMora(c.codigo, objetos);
      if (onde.zona || onde.objeto) lugares.push({ ...c, zona: onde.zona, objeto: onde.objeto });
      else naoReconhecidos.push(c.codigo);
    }
    const escrito = daPeca.principal?.trim() || naoReconhecidos[0] || null;
    saida.push({
      ...base, lugares, hoje: lugares.length === 0 ? escrito : null,
      ...(daPeca.area && lugares.length > 0 ? { area: daPeca.area.nome } : {}),
    });
  }
  return saida;
}

/**
 * Uma peça de pecasNoMapa, pronta como se a busca a tivesse achado — é o que
 * o ?peca= da URL abre ("Abrir no mapa grande" da folha, a volta da ficha).
 */
export function pecaDoMapa<T extends PecaComLocal & Omit<LinhaDaBusca, 'product_id'>>(
  productId: string,
  pecas: readonly T[],
  objetos: readonly Objeto[],
): PecaAchada | null {
  const linha = pecas.find((p) => p.product_id === productId && !p.extra)
    ?? pecas.find((p) => p.product_id === productId);
  return linha ? juntarResultados([linha], pecas, objetos)[0] ?? null : null;
}

/** Peças achadas por zona, para as etiquetas do desenho ("Z3 · 4"). A peça conta UMA vez por zona. */
export function achadasPorZona(achadas: readonly PecaAchada[]): Partial<Record<CodigoZona, number>> {
  const porZona = new Map<CodigoZona, Set<string>>();
  for (const p of achadas) {
    for (const l of p.lugares) {
      if (!l.zona) continue;
      const s = porZona.get(l.zona) ?? new Set<string>();
      s.add(p.product_id);
      porZona.set(l.zona, s);
    }
  }
  return Object.fromEntries([...porZona].map(([z, s]) => [z, s.size]));
}

/** Os lugares desenhados de todas as peças achadas: é o que o desenho contorna. */
export function objetosAcesos(achadas: readonly PecaAchada[]): Set<string> {
  const s = new Set<string>();
  for (const p of achadas) for (const l of p.lugares) if (l.objeto) s.add(l.objeto.origem);
  return s;
}

/**
 * O recado fixo sobre o desenho: "lâmpada h4 · 5 peças em 7 lugares". Sem
 * peça mas com LUGAR achado (o código "PR-FR-D2", o nome "vonixx"), conta os
 * lugares — "nada achado" desmentia a lista logo abaixo (revisão).
 */
export function avisoDaBusca(termo: string, achadas: readonly PecaAchada[], lugaresAchados = 0): string {
  const comLugar = achadas.filter((p) => !semLugarNoMapa(p));
  const lugares = comLugar.reduce((s, p) => s + p.lugares.length, 0);
  const t = termo.trim();
  if (achadas.length === 0) {
    return lugaresAchados > 0
      ? `${t} · ${lugaresAchados} ${lugaresAchados === 1 ? 'lugar' : 'lugares'}`
      : `${t} · nada achado`;
  }
  if (comLugar.length === 0) {
    return `${t} · ${achadas.length} ${achadas.length === 1 ? 'peça' : 'peças'}, nenhuma com lugar no mapa`;
  }
  return `${t} · ${comLugar.length} ${comLugar.length === 1 ? 'peça' : 'peças'} em ${lugares} ${lugares === 1 ? 'lugar' : 'lugares'}`;
}

// --- Busca de LUGAR no mesmo campo ---------------------------------------------

// Os acentos, depois do NFD, são as marcas combinantes de U+0300 a U+036F.
const ACENTOS = new RegExp(`[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`, 'g');
const semAcento = (s: string) => s.normalize('NFD').replace(ACENTOS, '').toLowerCase();
/** O código sem hífen, espaço nem caixa: "gc fr-d" e "GC-FR-D" são o mesmo. */
const compacto = (s: string) => semAcento(s).replace(/[^a-z0-9]/g, '');
const palavras = (s: string) => semAcento(s).split(/[^a-z0-9]+/).filter(Boolean);

/**
 * Os lugares que casam com o termo, para o grupo "Lugares" da busca e o filtro
 * da lista "Endereços da loja". Casa por:
 *  - CÓDIGO, com ou sem hífen e em qualquer caixa ("gc-fr-d", "PRFRV1"), pelo começo;
 *  - NOME do objeto ("vonixx", "painel vermelho") e EXTENSO ("corredor 1 fiat"),
 *    sem acento e por começo de palavra — cada palavra do termo começa alguma
 *    palavra do lugar, como no balcão.
 * Lugar VAZIO também aparece: achar onde pôr é metade do trabalho. E um código
 * COMPLETO de gancho (GC-FR-D-01-05) traz o gancho em si, não só a parede —
 * a lista agrupa os ganchos de uma parede numa linha só.
 *
 * Ordem: o código exato primeiro, depois quem casa pelo código, e por fim
 * quem casa pelo nome ou extenso; dentro de cada grupo, a ordem da lista.
 */
export function acharLugares(
  termo: string,
  lugares: readonly LugarComEndereco[],
  objetos: ReadonlyArray<{ origem: string; codigo?: string; nome?: string }>,
  pecasPorObjeto: ReadonlyMap<string, number> = new Map(),
): LugarComEndereco[] {
  const t = compacto(termo);
  const doTermo = palavras(termo);
  if (t.length < 2 || doTermo.length === 0) return [];

  const exatos: LugarComEndereco[] = [];
  const porCodigo: LugarComEndereco[] = [];
  const porNome: LugarComEndereco[] = [];
  for (const l of lugares) {
    const c = compacto(l.codigo);
    if (c === t) { exatos.push(l); continue; }
    if (c.startsWith(t)) { porCodigo.push(l); continue; }
    const doLugar = palavras(`${l.codigo} ${l.nome} ${l.descricao}`);
    if (doTermo.every((p) => doLugar.some((w) => w.startsWith(p)))) porNome.push(l);
  }

  // O gancho (ou qualquer lugar) cujo código é EXATAMENTE o digitado e que a
  // lista não tem como linha própria — os ganchos de uma parede viram uma linha.
  const jaTem = new Set([...exatos, ...porCodigo, ...porNome].map((l) => l.codigo.toUpperCase()));
  const individuais: LugarComEndereco[] = [];
  for (const o of objetos) {
    const codigo = o.codigo?.trim().toUpperCase();
    if (!codigo || compacto(codigo) !== t || jaTem.has(codigo)) continue;
    // A ÁREA vem do próprio código (GC-FR-… é a frente); fora da regra, a
    // lista de endereços não tem onde pôr a linha, e ela fica de fora.
    const area = codigo.split(/\s*-\s*/)[1];
    if (!area || !(area in AREAS)) continue;
    jaTem.add(codigo);
    individuais.push({
      codigo,
      descricao: descreverEndereco(codigo) ?? '',
      nome: o.nome ?? '',
      origem: o.origem,
      area: area as Area,
      pecas: pecasPorObjeto.get(o.origem) ?? 0,
      ganchos: 0,
    });
  }
  return [...individuais, ...exatos, ...porCodigo, ...porNome];
}

// --- Quem já mora no destino (fase 3) ------------------------------------------

const naRegua = (s: string | null | undefined) => (s ?? '').replace(/\s/g, '').toUpperCase();

/**
 * Quem já mora no lugar `destino` — para o "Em Z já está: W" da mudança e do
 * "Preencher". Casa como o mapa casa (ondeMora: o objeto de código mais longo),
 * então a peça em PR-FR-V1-01-05 mora na vaga, não na prateleira. Lugar EXTRA
 * conta (a calota que também fica no chão está no chão). A própria peça que
 * está mudando não conta, e cada peça aparece uma vez.
 *
 * Dividir lugar é legítimo (decisão de 11/08/2026): isto é aviso, nunca bloqueio.
 */
export function ocupantesDe<T extends PecaComLocal>(
  destino: string,
  pecas: readonly T[],
  objetos: readonly Objeto[],
  ignorar: string | null = null,
): T[] {
  if (!destino.trim()) return [];
  const alvo = ondeMora(destino, objetos).objeto;
  const vistos = new Set<string>();
  const saida: T[] = [];
  for (const p of pecas) {
    if (p.product_id === ignorar || vistos.has(p.product_id) || !temLugar(p.local)) continue;
    const mora = alvo
      ? ondeMora(p.local, objetos).objeto?.origem === alvo.origem
      : naRegua(p.local) === naRegua(destino);
    if (!mora) continue;
    vistos.add(p.product_id);
    saida.push(p);
  }
  return saida;
}

/** Quantas peças moram em cada objeto do desenho (o principal e os extras contam). */
export function pecasPorObjeto(
  pecas: readonly PecaComLocal[],
  objetos: readonly Objeto[],
): Map<string, number> {
  const m = new Map<string, number>();
  for (const p of pecas) {
    const o = ondeMora(p.local, objetos).objeto;
    if (o) m.set(o.origem, (m.get(o.origem) ?? 0) + 1);
  }
  return m;
}
